import BaseTibbit from '@appblocks/codegeneration/Tibbits/_BaseTibbit';
import Tibbits from '@appblocks/codegeneration/Tibbits/index';
import {
    CodeBlock,
    BlockType,
    SettingType,
    TibboDataType,
} from '@appblocks/codegeneration/types';

export default class Tibbit_13 extends BaseTibbit {
    constructor() {
        super();
        this.id = 'tibbit_13';
        this.name = 'Tibbit 13';
        this.fileLinks = [
            // 'ssi\\ssi.tbs',
            // 'ssi\\ssi.tbh',
        ];
        this.files = [
            'libraries/super_i2c/super_i2c.tbh',
            'libraries/super_i2c/super_i2c.tbs',
            'libraries/tibbits/tbt13/tbt13.tbh',
            'libraries/tibbits/tbt13/tbt13.tbs',
            'libraries/ssi/ssi.tbh',
            'libraries/ssi/ssi.tbs',
        ];

        const settings = [
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.NAME,
                required: true,
                default: '',
            },
            {
                id: 'CH1',
                name: 'Channel 1',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: '',
            },
            {
                id: 'CH2',
                name: 'Channel 2',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: '',
            },
            {
                id: 'CH3',
                name: 'Channel 3',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: '',
            },
            {
                id: 'CH4',
                name: 'Channel 4',
                settingType: SettingType.NAME,
                dataType: TibboDataType.FLOAT,
                required: true,
                default: '',
            },
            {
                id: 'USESSI',
                name: 'Mode',
                settingType: SettingType.OPTION,
                required: true,
                readonly: true,
                default: 'NO',
                options: [
                    {
                        name: 'I2C mode',
                        value: 'YES',
                    },
                    {
                        name: 'Direct I/O (bit-banging)',
                        value: 'NO',
                    },
                ],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];

        if (this.project.runtime === 'simulator') {
            return [];
        }

        const socket = Tibbits.getSocket(this.getSettingValue('SOCKET'), this.project.device.id);

        const NAME = this.getSettingValue('NAME');
        const CH1 = this.getSettingValue('CH1');
        const CH2 = this.getSettingValue('CH2');
        const CH3 = this.getSettingValue('CH3');
        const CH4 = this.getSettingValue('CH4');
        const USESSI = this.getSettingValue('USESSI');

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: `
    'Tibbit 13 setup on socket ${this.getSettingValue('SOCKET')}
    '================================================================
    tbt13_init("${NAME}",${NAME}_DATA,${NAME}_CLK,${NAME}_CHANNEL_NUM,${USESSI})
    
    'To get ADC data of each channel:

    'dim adc1, adc2, adc3, adc4 as integer
    'tbt13_channel_select(ADC_1,${NAME}_CHANNEL_NUM)    'select the channel
    'tbt13_get_prev(${this.getSettingValue('NAME')}_CHANNEL_NUM)                'discard first read, can use tbt13_read_prev() or tbt13_read_prev_mv() for that
    'adc1=tbt13_get_prev_mv(${this.getSettingValue('NAME')}_CHANNEL_NUM)        'now get the data

    'tbt13_channel_select(ADC_2,${this.getSettingValue('NAME')}_CHANNEL_NUM)    'onto the next channel...
    'tbt13_get_prev(${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'adc2=tbt13_get_prev_mv(${this.getSettingValue('NAME')}_CHANNEL_NUM)

    'tbt13_channel_select(ADC_3,${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'tbt13_get_prev(${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'adc3=tbt13_get_prev_mv(${this.getSettingValue('NAME')}_CHANNEL_NUM)

    'tbt13_channel_select(ADC_4,${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'tbt13_get_prev(${this.getSettingValue('NAME')}_CHANNEL_NUM)
    'adc4=tbt13_get_prev_mv(${this.getSettingValue('NAME')}_CHANNEL_NUM)

    'sys.debugprint("CH1:"+stri(adc1)+" CH2:"+stri(adc2)+" CH3:"+stri(adc3)+" CH4:"+stri(adc4)+chr(13)+chr(10))
    '-----------------------------------------------------------------

        `,
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const ${NAME}_CLK=${socket?.pins[0]}
const ${NAME}_DATA=${socket?.pins[1]}`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare ${NAME}_CHANNEL_NUM as byte
        `,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim ${NAME}_CHANNEL_NUM as byte
        `,
        });

        blocks.push({
            location: `var_${CH1}_update`,
            blockType: BlockType.CODE,
            template: `
    tbt13_channel_select(ADC_1,${NAME}_CHANNEL_NUM) 'select the channel
    tbt13_get_prev(${NAME}_CHANNEL_NUM)
    var_${CH1}_update_completed(tbt13_get_prev_mv(${NAME}_CHANNEL_NUM) / 1000.0)
    `,
        });
        blocks.push({
            location: `var_${CH2}_update`,
            blockType: BlockType.CODE,
            template: `
    tbt13_channel_select(ADC_2,${NAME}_CHANNEL_NUM) 'select the channel
    tbt13_get_prev(${NAME}_CHANNEL_NUM)
    var_${CH2}_update_completed(tbt13_get_prev_mv(${NAME}_CHANNEL_NUM) / 1000.0)
    `,
        });
        blocks.push({
            location: `var_${CH3}_update`,
            blockType: BlockType.CODE,
            template: `
    tbt13_channel_select(ADC_3,${NAME}_CHANNEL_NUM) 'select the channel
    tbt13_get_prev(${NAME}_CHANNEL_NUM)
    var_${CH3}_update_completed(tbt13_get_prev_mv(${NAME}_CHANNEL_NUM) / 1000.0)
    `,
        });
        blocks.push({
            location: `var_${CH4}_update`,
            blockType: BlockType.CODE,
            template: `
    tbt13_channel_select(ADC_4,${NAME}_CHANNEL_NUM) 'select the channel
    tbt13_get_prev(${NAME}_CHANNEL_NUM)
    var_${CH4}_update_completed(tbt13_get_prev_mv(${NAME}_CHANNEL_NUM) / 1000.0)
    `,
        });


        return blocks;
    }
}
